import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ChatMessage } from "types/ChatbotTypes";
import { initialChatbotState } from "./chatbotState";
import { sendMessage, resetSession, endSession, getAvailableDatasets } from "requests/chatbotApi";
import { RootState } from "app/store";
import { ApiException } from "app/ApiClient";
import { getSessionId, renewSessionId, clearSessionId } from "utils/chatbotSessionStorage";

const getNestedErrorDetail = (errorMessage: unknown): string | undefined => {
  if (typeof errorMessage !== "string") return undefined;

  try {
    const parsedErrorMessage = JSON.parse(errorMessage) as { detail?: unknown };
    return typeof parsedErrorMessage.detail === "string" ? parsedErrorMessage.detail : undefined;
  } catch {
    return undefined;
  }
};

const getErrorMessage = (error: unknown): string => {
  if (ApiException.isApiException(error)) {
    try {
      const parsed = JSON.parse(error.response) as { detail?: string; errorMessage?: string };
      if (parsed.detail) return parsed.detail;
      if (parsed.errorMessage) {
        const nestedDetail = getNestedErrorDetail(parsed.errorMessage);
        if (nestedDetail) return nestedDetail;
        return parsed.errorMessage;
      }
    } catch {
      // response is not JSON
    }
    if (error.response) return error.response;
  }
  if (error instanceof Error) return error.message;
  return "An unexpected error occurred. Please contact your administrator.";
};

export const fetchAvailableDatasets = createAsyncThunk(
  "chatbot/fetchAvailableDatasets",
  async (_, { rejectWithValue }) => {
    try {
      return await getAvailableDatasets();
    } catch (error) {
      return rejectWithValue("Failed to load available datasets");
    }
  },
);

export const sendChatMessage = createAsyncThunk(
  "chatbot/sendMessage",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { chatbot, datasetsData } = state;
      const { activeDatasetId, sessions } = chatbot;
      if (!activeDatasetId || !sessions[activeDatasetId]) {
        return rejectWithValue("No active dataset");
      }
      const session = sessions[activeDatasetId];
      const lastUserMessage = [...session.messages].reverse().find((m) => m.role === "user");
      if (!lastUserMessage) {
        return rejectWithValue("No user message found");
      }
      const encodingVersion = datasetsData.currentVersionId;
      if (!encodingVersion) {
        return rejectWithValue("Dataset version is not yet available. Please wait and try again.");
      }
      const result = await sendMessage({
        sessionId: getSessionId(activeDatasetId),
        datasetId: activeDatasetId,
        encodingVersion,
        message: lastUserMessage.content,
      });
      return result.response ?? "";
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const resetChatSession = createAsyncThunk(
  "chatbot/resetSession",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { chatbot, datasetsData } = state;
      const { activeDatasetId } = chatbot;
      if (!activeDatasetId) {
        return rejectWithValue("No active dataset");
      }
      if (!datasetsData.currentVersionId) {
        return rejectWithValue("Dataset version is not yet available. Please wait and try again.");
      }
      await resetSession({
        sessionId: getSessionId(activeDatasetId),
        datasetOriginId: activeDatasetId,
        encodingVersion: datasetsData.currentVersionId,
      });
      renewSessionId(activeDatasetId);
      return activeDatasetId;
    } catch (error) {
      return rejectWithValue("Failed to reset session");
    }
  },
);

export const endChatSession = createAsyncThunk(
  "chatbot/endSession",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { chatbot, datasetsData } = state;
      const { activeDatasetId } = chatbot;
      if (!activeDatasetId) {
        return rejectWithValue("No active dataset");
      }
      await endSession({
        sessionId: getSessionId(activeDatasetId),
        datasetOriginId: activeDatasetId,
        encodingVersion: datasetsData.currentVersionId ?? "",
      });
      clearSessionId(activeDatasetId);
      return activeDatasetId;
    } catch (error) {
      return rejectWithValue("Failed to end session");
    }
  },
);

const chatbotSlice = createSlice({
  name: "chatbot",
  initialState: initialChatbotState,
  reducers: {
    toggleChatbot: (state) => {
      state.isOpen = !state.isOpen;
    },
    openChatbot: (state) => {
      state.isOpen = true;
    },
    closeChatbot: (state) => {
      state.isOpen = false;
    },
    setPanelWidth: (state, { payload }: PayloadAction<number>) => {
      state.panelWidth = payload;
    },
    setActiveDataset: (state, { payload }: PayloadAction<{ datasetId: string; datasetName: string }>) => {
      state.activeDatasetId = payload.datasetId;
      if (!state.sessions[payload.datasetId]) {
        state.sessions[payload.datasetId] = {
          datasetId: payload.datasetId,
          datasetName: payload.datasetName,
          messages: [],
        };
      }
    },
    addUserMessage: (state, { payload }: PayloadAction<{ content: string }>) => {
      const { activeDatasetId, sessions } = state;
      if (!activeDatasetId || !sessions[activeDatasetId]) return;
      const message: ChatMessage = {
        id: `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        role: "user",
        content: payload.content,
        timestamp: Date.now(),
      };
      sessions[activeDatasetId].messages.push(message);
      sessions[activeDatasetId].draftInput = "";
    },
    setDraftInput: (state, { payload }: PayloadAction<string>) => {
      const { activeDatasetId, sessions } = state;
      if (!activeDatasetId || !sessions[activeDatasetId]) return;
      sessions[activeDatasetId].draftInput = payload;
    },
    addAssistantMessage: (state, { payload }: PayloadAction<{ content: string }>) => {
      const { activeDatasetId, sessions } = state;
      if (!activeDatasetId || !sessions[activeDatasetId]) return;
      const message: ChatMessage = {
        id: `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        role: "assistant",
        content: payload.content,
        timestamp: Date.now(),
      };
      sessions[activeDatasetId].messages.push(message);
    },
    removeLastAssistantMessage: (state) => {
      const { activeDatasetId, sessions } = state;
      if (!activeDatasetId || !sessions[activeDatasetId]) return;
      const messages = sessions[activeDatasetId].messages;
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === "assistant") {
          messages.splice(i, 1);
          break;
        }
      }
    },
    clearSession: (state, { payload }: PayloadAction<string>) => {
      delete state.sessions[payload];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendChatMessage.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(sendChatMessage.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        const { activeDatasetId, sessions } = state;
        if (!activeDatasetId || !sessions[activeDatasetId]) return;
        const message: ChatMessage = {
          id: `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
          role: "assistant",
          content: payload,
          timestamp: Date.now(),
        };
        sessions[activeDatasetId].messages.push(message);
      })
      .addCase(sendChatMessage.rejected, (state, { payload }) => {
        state.isLoading = false;
        const { activeDatasetId, sessions } = state;
        if (!activeDatasetId || !sessions[activeDatasetId]) return;
        const message: ChatMessage = {
          id: `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
          role: "assistant",
          content: `⚠️ ${payload as string}`,
          timestamp: Date.now(),
        };
        sessions[activeDatasetId].messages.push(message);
      })
      .addCase(resetChatSession.fulfilled, (state, { payload }) => {
        if (payload && state.sessions[payload]) {
          state.sessions[payload].messages = [];
        }
      })
      .addCase(endChatSession.fulfilled, (state, { payload }) => {
        if (payload) {
          delete state.sessions[payload];
        }
      })
      .addCase(fetchAvailableDatasets.pending, (state) => {
        state.availableDatasetsLoading = true;
      })
      .addCase(fetchAvailableDatasets.fulfilled, (state, { payload }) => {
        state.availableDatasetsLoading = false;
        state.availableDatasets = payload;
      })
      .addCase(fetchAvailableDatasets.rejected, (state) => {
        state.availableDatasetsLoading = false;
        state.availableDatasets = [];
      });
  },
});

export const {
  toggleChatbot,
  openChatbot,
  closeChatbot,
  setPanelWidth,
  setActiveDataset,
  setDraftInput,
  addUserMessage,
  addAssistantMessage,
  removeLastAssistantMessage,
  clearSession,
} = chatbotSlice.actions;

export default chatbotSlice.reducer;
